import { useEffect, type ReactNode } from 'react'

/*
 * O dicionário é texto de produto, não documentação técnica: quem lê é o estrategista
 * diante da matriz. Por isso um único exemplo (funk, mulheres 18–34, Pepsi) atravessa
 * todas as seções com os mesmos números — cada conceito se apoia no anterior em vez de
 * inventar um cenário novo. Se mudar um número do exemplo, confira os que dependem dele.
 */

const SECTIONS: { id: string; title: string; terms: [string, string][] }[] = [
  {
    id: 'como-ler',
    title: 'Como ler um número',
    terms: [['indice', 'Índice base 100'], ['tres-universos', 'Os três universos']],
  },
  {
    id: 'o-ponto',
    title: 'Os quatro números de um ponto',
    terms: [
      ['afinidade', 'Afinidade'],
      ['alcance', 'Alcance'],
      ['saturacao', 'Afinidade competitiva'],
      ['ocupacao', 'Ocupação'],
    ],
  },
  {
    id: 'decisao',
    title: 'Da matriz à decisão',
    terms: [
      ['quadrantes', 'Quadrantes'],
      ['prioridade', 'Prioridade'],
      ['permissao', 'Permissão de marca'],
      ['relevancia', 'Relevância por dimensão'],
    ],
  },
  {
    id: 'confianca',
    title: 'Quanto confiar no número',
    terms: [
      ['base', 'Base não ponderada'],
      ['base-efetiva', 'Base efetiva'],
      ['celula', 'Célula'],
      ['significancia', 'Intervalo e significância'],
      ['valor-q', 'Valor-q'],
      ['supressao', 'Supressão'],
      ['fragilidade', 'Fragilidade à vista'],
    ],
  },
  {
    id: 'territorios',
    title: 'De onde vêm os territórios',
    terms: [
      ['territorio', 'Território e pilar'],
      ['enquadramento', 'Enquadramento'],
      ['tautologia', 'Tautologia de audiência'],
    ],
  },
  {
    id: 'contexto',
    title: 'O contexto da análise',
    terms: [
      ['concorrentes', 'Marca foco e concorrentes'],
      ['funil', 'Etapa do funil'],
      ['wave', 'Wave'],
      ['horizonte', 'Horizonte'],
      ['procedencia', 'Procedência'],
    ],
  },
  {
    id: 'leitura',
    title: 'A leitura estratégica',
    terms: [['racional', 'Racional estratégico'], ['playbook', 'Playbook'], ['ressalvas', 'Ressalvas']],
  },
  { id: 'limiares', title: 'Limiares de referência', terms: [] },
]

const TITLES = new Map(SECTIONS.flatMap((s) => [[s.id, s.title] as const, ...s.terms]))

export function Dictionary() {
  // O link vem de um balão `?` com recarga de página: o navegador procura a âncora antes de
  // o React montar o texto, então o salto é refeito aqui.
  useEffect(() => {
    if (window.location.hash) document.querySelector(window.location.hash)?.scrollIntoView()
  }, [])

  return (
    <>
      <section className="home-hero">
        <div>
          <p className="eyebrow">Dicionário</p>
          <h1>O que cada número <em>quer dizer.</em></h1>
        </div>
        <div className="hero-deck">
          <p>
            Cada conceito da tela explicado em três perguntas: o que o número mede, contra quem
            ele foi comparado e o que ele não consegue dizer.
          </p>
          <span>Um exemplo só, do começo ao fim: funk entre mulheres de 18 a 34 anos</span>
        </div>
      </section>

      <div className="dictionary-body">
        <nav className="dictionary-index" aria-label="Índice do dicionário">
          <p className="eyebrow">Conceitos</p>
          <ol>
            {SECTIONS.map((section) => (
              <li key={section.id}>
                <a href={`#${section.id}`}>{section.title}</a>
                {section.terms.length > 0 && (
                  <ul>
                    {section.terms.map(([id, title]) => (
                      <li key={id}><a href={`#${id}`}>{title}</a></li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ol>
        </nav>

        <article className="dictionary-text">
          <Section id="como-ler">
            <Term id="indice">
              <p>
                Quase todo número da tela é um <strong>índice base 100</strong>. Ele responde a uma
                pergunta só: <em>quantas vezes mais (ou menos) do que a referência?</em>
              </p>
              <ul>
                <li><strong>100</strong> — igual à referência.</li>
                <li><strong>150</strong> — uma vez e meia a referência.</li>
                <li><strong>50</strong> — metade da referência.</li>
              </ul>
              <p>
                O que muda de uma métrica para outra é <strong>quem é a referência</strong>. Guarde
                isso e nenhum número da matriz será lido pelo outro.
              </p>
            </Term>

            <Term id="tres-universos">
              <p>Toda análise convive com três grupos de pessoas:</p>
              <ul>
                <li>
                  <strong>População</strong> — o mercado inteiro. Ex.: todos os brasileiros da
                  pesquisa.
                </li>
                <li>
                  <strong>Audiência</strong> — o recorte que a análise serve. Ex.: mulheres de 18 a
                  34 anos.
                </li>
                <li>
                  <strong>Complemento</strong> — quem está na população e não está na audiência.
                  Não aparece na tela, mas é contra ele que a significância é testada: a audiência
                  faz parte da população, então comparar uma com a outra seria comparar um grupo
                  consigo mesmo.
                </li>
              </ul>
            </Term>
          </Section>

          <Section id="o-ponto">
            <p className="dictionary-lead">
              Cada ponto da matriz carrega quatro números. Eles respondem a perguntas diferentes —
              e têm firmezas diferentes, porque cada um se apoia num grupo de pessoas de tamanho
              diferente.
            </p>

            <Term id="afinidade" tag="eixo X">
              <Question>Este território é característico da minha audiência?</Question>
              <Reference>a população.</Reference>
              <Example>
                Na população, 10% das pessoas curtem funk. Entre as mulheres de 18 a 34, são 22%.
                22 ÷ 10 = 2,2 → <strong>afinidade 220</strong>.
              </Example>
              <p>
                Em palavras: funk é bem mais da sua audiência do que do Brasil em geral. É o
                território dela, não de todo mundo.
              </p>
              <p>
                Abaixo do limiar do brief (padrão 110), o território não distingue a audiência e
                vira descarte. A afinidade é o número mais firme do ponto — usa a amostra inteira.
              </p>
              <Limit>
                Tamanho. Afinidade 300 sobre 1% da audiência é nicho, não oportunidade. Para isso
                existe o alcance.
              </Limit>
            </Term>

            <Term id="alcance" tag="tamanho da bolha">
              <Question>Quantas pessoas da minha audiência estão nesse território?</Question>
              <Reference>nenhuma — é a porcentagem da audiência.</Reference>
              <Example>
                22% das mulheres de 18 a 34 curtem funk → <strong>alcance 22%</strong>, ou 22 em
                cada 100.
              </Example>
              <p>
                É o que impede a matriz de vender índice alto sobre meia dúzia de pessoas: a bolha
                pequena avisa antes de qualquer texto. No ranking, o alcance é medido contra o
                maior alcance da própria matriz — "grande" é sempre em relação aos vizinhos.
              </p>
            </Term>

            <Term id="saturacao" tag="eixo Y">
              <Question>Os clientes dos meus concorrentes já estão nesse território?</Question>
              <Reference>a média da própria audiência.</Reference>
              <p>
                É o número que mais confunde, então vamos devagar. A afinidade competitiva olha só para dentro
                da audiência e pergunta: quem lá dentro já é cliente do concorrente gosta mais
                desse território do que o resto?
              </p>
              <Example>
                Das mulheres de 18 a 34, 22% curtem funk. Agora separe só as que bebem Pepsi: entre
                elas, 33% curtem funk. 33 ÷ 22 = 1,5 → <strong>afinidade competitiva 150</strong>.
              </Example>
              <p>
                Em palavras: <strong>dentro do seu público</strong>, quem já é cliente da Pepsi
                está no funk uma vez e meia mais do que a média. A Pepsi chegou lá antes — o
                território é disputado.
              </p>
              <p>Para calibrar a leitura:</p>
              <ul>
                <li>
                  <strong>150</strong> — o cliente do concorrente está muito mais no território que
                  o público médio. Disputado.
                </li>
                <li>
                  <strong>100</strong> — está no funk na mesma medida que qualquer pessoa do
                  público. O concorrente não tem vantagem ali.
                </li>
                <li>
                  <strong>70</strong> — está menos no funk que a média. Território longe dele.
                </li>
              </ul>
              <p>
                Com vários concorrentes, vale o <strong>mais forte</strong>, não a média. Se a Pepsi
                faz 150 e o Guaraná Antarctica faz 95, a afinidade competitiva é 150: basta uma marca dominando
                para o território estar ocupado. Por isso o painel sempre diz qual concorrente
                puxou o número.
              </p>
              <p>
                Por que a referência é a audiência, e não o Brasil? Porque o Brasil já está no eixo
                X. Se a afinidade competitiva também fosse medida contra a população, ela carregaria a
                afinidade junto, e quase todo território com afinidade alta pareceria disputado.
              </p>
              <p>
                "Cliente", aqui, é atalho: quem conta é o público da marca na{' '}
                <a href="#funil">etapa do funil</a> que o KPI pede. Num brief de awareness, é
                quem conhece a Pepsi; num de lealdade, quem compra.
              </p>
              <Limit>
                Investimento. Afinidade competitiva 150 não quer dizer que a Pepsi patrocina funk — quer dizer
                que os clientes dela estão lá. Pode ser estratégia, pode ser coincidência de
                perfil.
              </Limit>
            </Term>

            <Term id="ocupacao" tag="cor do ponto">
              <Question>Quem detém esse território hoje?</Question>
              <p>
                A conta é a mesma da afinidade competitiva, só que para a sua marca: entre as mulheres de 18 a
                34 que já são suas clientes, quantas curtem funk, comparado à média do público?
              </p>
              <Example>
                Entre as suas clientes, 18% curtem funk. 18 ÷ 22 = 0,82 → índice 82, abaixo de 110.
                A Pepsi está em 150, acima. Cor: <strong>Concorrente</strong>.
              </Example>
              <p>
                Como na afinidade competitiva, "cliente" é a <a href="#funil">etapa do funil</a> do KPI — a
                mesma para a sua marca e para os concorrentes, senão a cor e o eixo Y
                comparariam coisas diferentes.
              </p>
              <table className="dictionary-table">
                <thead><tr><th>Cor</th><th>Sua marca</th><th>Concorrente</th><th>Leitura</th></tr></thead>
                <tbody>
                  <tr><td>Minha marca</td><td>alta</td><td>baixa</td><td>terreno próprio</td></tr>
                  <tr><td>Disputada</td><td>alta</td><td>alta</td><td>as duas estão lá</td></tr>
                  <tr><td>Concorrente</td><td>baixa</td><td>alta</td><td>demanda formada por outro</td></tr>
                  <tr><td>Branca</td><td>baixa</td><td>baixa</td><td>ninguém plantou bandeira</td></tr>
                </tbody>
              </table>
              <Limit>
                Muito, quando a marca é pequena. A ocupação se apoia nos clientes de uma marca só,
                dentro da audiência — o grupo mais estreito dos quatro. É a primeira coisa a ficar
                incerta num ponto.
              </Limit>
            </Term>
          </Section>

          <Section id="decisao">
            <Term id="quadrantes">
              <p>
                O quadrante resume os índices num rótulo. A primeira pergunta é sempre a
                afinidade: se o território não é da audiência, o resto não importa.
              </p>
              <dl className="dictionary-defs">
                <dt>Descarte</dt>
                <dd>
                  Afinidade abaixo do limiar. A audiência não está mais ali do que o Brasil em
                  geral. Continua no gráfico — ver o que foi descartado também informa —, mas sai da
                  recomendação e do ranking, por maior que seja a prioridade calculada.
                </dd>
                <dt>Fortaleza</dt>
                <dd>
                  Afinidade alta e a sua marca acima do limiar, indexando pelo menos tanto quanto o
                  concorrente mais forte.
                  Onde você já é referência. Não é onde há mais a ganhar, é onde há mais a perder:
                  defender e aprofundar.
                </dd>
                <dt>Campo de batalha</dt>
                <dd>
                  Afinidade alta, afinidade competitiva alta e a marca sem dominar. É o funk do exemplo. O
                  público já está lá e já foi educado — por outro. Custa mais caro, mas a demanda
                  existe.
                </dd>
                <dt>Território branco</dt>
                <dd>
                  Afinidade alta e nenhum concorrente forte. A audiência está ali e ninguém chegou.
                  Atenção: ninguém estar lá às vezes quer dizer que ninguém achou que valia — é aqui
                  que a permissão de marca mais pesa.
                </dd>
              </dl>
            </Term>

            <Term id="prioridade">
              <p>
                Um número de 0 a 1 que ordena os territórios. Quatro métricas em 50 pontos não se
                ordenam sozinhas: alguém precisa dizer o que pesa mais. Quem diz é o{' '}
                <strong>KPI do brief</strong> — e ele muda a conta, não só o texto.
              </p>
              <table className="dictionary-table">
                <thead><tr><th>KPI</th><th>O que o ranking procura</th></tr></thead>
                <tbody>
                  <tr><td>Awareness</td><td>território grande e livre — alcance e espaço vazio mandam</td></tr>
                  <tr><td>Consideração</td><td>roubo de share — onde o concorrente indexa alto e a marca não</td></tr>
                  <tr><td>Intenção</td><td>quem já está perto — a força da própria marca pesa mais</td></tr>
                  <tr><td>Conversão</td><td>como intenção, mas exige mais volume</td></tr>
                  <tr><td>Lealdade</td><td>defender a base — onde a marca já é forte</td></tr>
                </tbody>
              </table>
              <p>
                No exemplo, o funk sobe muito com <strong>consideração</strong> (a Pepsi está lá e
                você não) e cai com <strong>lealdade</strong> (não é a sua base). Mesmo dado, outra
                ordem — porque o objetivo é outro.
              </p>
            </Term>

            <Term id="permissao">
              <p>
                Um território pode ter afinidade altíssima e a marca não ter credibilidade nenhuma
                nele — banco patrocinando festival de trap. A <strong>permissão de marca</strong> é
                um multiplicador de 0 a 1, com justificativa obrigatória, que alguém aplica sobre a
                prioridade.
              </p>
              <p>
                Ela nunca se mistura ao score original: os dois aparecem lado a lado. Assim dá para
                saber se um território caiu no ranking por causa do dado ou por causa de uma
                opinião — e de quem.
              </p>
            </Term>

            <Term id="relevancia" tag="radar">
              <Question>Quanto esta dimensão importa para a minha marca?</Question>
              <p>
                A afinidade diz o que distingue a audiência, não o que importa para a marca. Para
                uma marca de biscoito, "Saúde" pode ser o que mais separa o público do resto do
                Brasil e, mesmo assim, não ter nada a ver com ela. Só quem planeja sabe — então é
                julgamento, como a permissão.
              </p>
              <p>
                Cada território pertence a uma <strong>pasta</strong> da própria pesquisa: Alimentos
                e bebidas, Saúde e bem-estar, Lazer e Entretenimento... Diferente do pilar, que a IA
                escreve, a pasta é do Profiles e não muda de uma análise para outra. No radar, cada
                fatia é uma pasta: clique ou arraste dentro dela — o centro é 0, a borda é 1.
              </p>
              <Example>
                Peso 0,5 em Lazer e Entretenimento: o funk, que estava com prioridade 0,60, passa a
                0,30 no ranking. A prioridade original continua ao lado.
              </Example>
              <p>
                O peso multiplica a prioridade junto com a permissão de marca. Fatia cinza é pasta
                cujos territórios estão todos em descarte — o peso dela não move nada no ranking.
              </p>
              <Limit>
                O gráfico. Afinidade, afinidade competitiva, alcance e ocupação são dado de pesquisa e não
                mudam com o peso; só a ordem do ranking muda. A leitura estratégica já gerada
                também não — é preciso gerar outra.
              </Limit>
            </Term>
          </Section>

          <Section id="confianca">
            <p className="dictionary-lead">
              Um índice só vale o tanto de gente que o sustenta. Esta seção diz como enxergar isso.
            </p>

            <Term id="base">
              <p>
                Quantas pessoas de verdade responderam — sem peso nenhum. É ela que decide se um
                número aparece: índice 180 sobre 30 respondentes é ruído, e peso estatístico não
                transforma ruído em informação.
              </p>
              <ul>
                <li><strong>Menos de 50</strong> — o número é suprimido.</li>
                <li><strong>De 50 a 100</strong> — aparece, com alerta de base baixa.</li>
              </ul>
            </Term>

            <Term id="base-efetiva">
              <p>
                A pesquisa pondera as respostas para representar a população. Isso corrige o
                retrato, mas tem um custo: 1.000 respostas ponderadas valem, em informação, menos
                que 1.000 respostas. A <strong>base efetiva</strong> (de Kish) diz quanto valem —
                talvez 800.
              </p>
              <p>
                É ela que entra nos testes estatísticos. Usar o número cheio faria tudo parecer mais
                certo do que é, sem nada na tela denunciar o erro.
              </p>
            </Term>

            <Term id="celula">
              <p>
                A base é o grupo inteiro; a <strong>célula</strong> é quem, dentro dele, está no
                território. E é a célula que costuma faltar.
              </p>
              <Example>
                Suponha 20 clientes da Pepsi na audiência. Se 33% curtem funk, a afinidade competitiva de 150
                vem de <strong>7 pessoas</strong>. Uma a mais ou a menos e o índice muda muito.
              </Example>
              <p>
                O piso é <strong>10 pessoas na célula</strong>. Uma base folgada não salva célula
                fina — por isso o painel do território mostra quantos respondentes sustentam cada
                eixo, ao lado do índice.
              </p>
            </Term>

            <Term id="significancia">
              <p>
                O <strong>intervalo de confiança</strong> (95%) é a faixa onde o índice
                provavelmente está. Afinidade 220 entre 190 e 250 é uma leitura firme; o mesmo 220
                entre 105 e 340 pode ser quase qualquer coisa.
              </p>
              <p>
                <strong>Significante</strong> quer dizer que a diferença é grande demais para ser
                sorte da amostra. O teste compara a audiência com o complemento — mulheres de 18 a
                34 contra todo o resto.
              </p>
            </Term>

            <Term id="valor-q">
              <p>
                Para achar territórios, a análise testa mais de mil recortes. Com tantos testes,
                alguns dão "significante" por puro acaso — centenas, a 95% de confiança. E eles
                tendem a parar no topo da lista, porque efeito grande sobre base pequena é
                justamente o que o acaso produz.
              </p>
              <p>
                O <strong>valor-q</strong> corrige isso: controla que fração dos achados aceitos
                pode ser falsa. É ele, e não o valor-p comum, que decide se um recorte vira
                território. Sem ele, a descoberta inventaria território.
              </p>
            </Term>

            <Term id="supressao">
              <p>
                Quando algo fica de fora por falta de gente, isso é <strong>sempre
                registrado</strong>. Um território que some calado é lido como "não há oportunidade
                aqui", quando o que faltou foi amostra.
              </p>
              <table className="dictionary-table">
                <thead><tr><th>Registro</th><th>O que aconteceu</th><th>Está no gráfico?</th></tr></thead>
                <tbody>
                  <tr><td>Território</td><td>base ou célula insuficiente na audiência</td><td>não</td></tr>
                  <tr><td>Concorrente</td><td>a afinidade competitiva veio de uma célula abaixo do piso</td><td>sim, com eixo Y frágil</td></tr>
                  <tr><td>Marca</td><td>a marca não tem ninguém na audiência, na etapa do funil medida</td><td>a marca inteira some</td></tr>
                </tbody>
              </table>
              <p>
                O registro de marca é o que permite ler "a Sollys não aparece" como falta de base,
                e não como falta de concorrência.
              </p>
            </Term>

            <Term id="fragilidade">
              <p>
                Dado frágil continua na tela, <strong>com o aviso junto</strong>. Esconder tiraria o
                território inteiro da mesa — inclusive uma afinidade que era sólida. O aviso aparece
                em três lugares:
              </p>
              <ul>
                <li>
                  <strong>No gráfico</strong> — contorno tracejado quando a afinidade competitiva é incerta,
                  preenchimento esmaecido quando a cor é.
                </li>
                <li><strong>No painel</strong> — a etiqueta "base fina" em cada eixo afetado.</li>
                <li>
                  <strong>Nas ressalvas</strong> — a leitura estratégica diz o que muda na decisão
                  por causa daquela fragilidade, não só que ela existe.
                </li>
              </ul>
            </Term>
          </Section>

          <Section id="territorios">
            <Term id="territorio">
              <p>
                Um <strong>território</strong> é algo que a audiência faz, curte ou pensa: funk,
                mudar de casa, estilo de vida secular. Cada ponto da matriz é um.
              </p>
              <p>
                Nunca é a "caixinha" em que a pessoa cai. Idade, gênero, renda, situação de emprego
                e segmentações compradas ficam de fora: servem para definir a audiência, não para
                ativá-la. Saúde e profissão ficam — para uma marca de saúde ou B2B, são dos
                territórios mais úteis.
              </p>
              <p>
                O <strong>pilar</strong> agrupa territórios por tema ("Cultura pop", "Bem-estar")
                para que 50 pontos possam ser lidos de uma vez.
              </p>
              <p>
                Por padrão, os territórios são <strong>descobertos</strong>: cerca de mil perguntas
                da pesquisa são testadas contra a audiência, e ficam as que a separam do resto. Uma
                lista fixa ficaria rasa — "música" faz 96 entre mulheres de 18 a 34; "funk" faz 220.
                Quando comparar análises importa mais, dá para usar uma lista curada.
              </p>
              <p>
                Respostas como "Não sei", "Não se aplica" e "Não perguntado" nunca viram território:
                dizem algo sobre o questionário, não sobre a pessoa.
              </p>
              <p>
                Também ficam de fora as perguntas que a YouGov marcou para sair da pesquisa: um
                território que desaparece na wave seguinte não pode ser conferido depois.
              </p>
            </Term>

            <Term id="enquadramento">
              <p>
                A descoberta acha o sinal, mas não sabe nomeá-lo. O rótulo cru pode ser "Discordo
                quase totalmente" — que só faz sentido ao lado da pergunta "Minha fé é importante
                para mim".
              </p>
              <p>
                No <strong>enquadramento</strong>, a IA lê os dois rótulos e devolve um nome
                utilizável e um pilar: <strong>Estilo de Vida Secular</strong>. Ela recebe só
                palavras, nenhum número — não tem como alterar uma métrica que nunca viu. O que não
                serve para ativação é marcado como tal, com o motivo, em vez de sumir.
              </p>
            </Term>

            <Term id="tautologia">
              <p>
                Um território que só repete a definição da audiência. Para "mulheres de 18 a 34",
                "ter entre 18 e 24 anos" indexaria altíssimo e não diria nada novo.
              </p>
              <p>
                Depende do brief: idade é tautologia para "mulheres 18–34" e é achado para
                "compradores de energético". Por isso o filtro roda contra a audiência de cada
                análise, e o cabeçalho avisa o que saiu — quem conhece o recorte estranharia não ver
                idade na lista.
              </p>
            </Term>
          </Section>

          <Section id="contexto">
            <Term id="concorrentes">
              <p>
                A <strong>marca foco</strong> é a do cliente. Os <strong>concorrentes</strong> são
                escolhidos no brief — a pesquisa não descobre quem compete com quem. Ela responde
                depois: "já que você considera Pepsi e Guaraná seus concorrentes, onde estão os
                clientes deles?"
              </p>
              <p>
                Por isso a lista muda o eixo Y inteiro. Tirar a Pepsi pode transformar o campo de
                batalha do funk em território branco. Incluir uma marca pequena não atrapalha (vale
                o concorrente mais forte); incluir uma gigante pode deixar quase tudo disputado.
              </p>
              <p>
                As marcas vêm das perguntas de marca feitas dentro do Profiles, não dos scores
                publicados do BrandIndex — os números não batem com um relatório BrandIndex. E a
                matriz não diz que um explica o outro: mostra onde os clientes estão, não por que
                escolheram a marca.
              </p>
            </Term>

            <Term id="funil">
              <p>
                A pesquisa pergunta sobre cada marca em várias etapas — conhecer, considerar,
                pretender comprar, ser cliente. O <strong>KPI do brief escolhe a etapa</strong>, e é
                dela que saem a afinidade competitiva e a ocupação:
              </p>
              <table className="dictionary-table">
                <thead><tr><th>KPI</th><th>Quem conta como público da marca</th></tr></thead>
                <tbody>
                  <tr><td>Awareness</td><td>quem conhece a marca</td></tr>
                  <tr><td>Consideração</td><td>quem consideraria a marca</td></tr>
                  <tr><td>Intenção e conversão</td><td>quem pretende comprar</td></tr>
                  <tr><td>Lealdade</td><td>quem é cliente</td></tr>
                </tbody>
              </table>
              <Example>
                Num brief de awareness, afinidade competitiva 150 no funk quer dizer que quem{' '}
                <strong>conhece</strong> a Pepsi está no funk uma vez e meia mais que a média do
                público — não necessariamente quem compra.
              </Example>
              <Limit>
                Muito, quando a marca é conhecida por quase todos. Se 9 em cada 10 pessoas da
                audiência conhecem a marca, "quem conhece" é quase a audiência inteira, e o índice
                fica perto de 100 em todo território.
              </Limit>
            </Term>

            <Term id="wave">
              <p>
                O Profiles é republicado toda semana; cada edição é uma <strong>wave</strong>, com
                data (ex.: 2026-10-18). Toda análise nova usa a wave mais recente e grava qual foi.
              </p>
              <p>
                É o que torna o número conferível. "Funk faz 220 entre mulheres de 18 a 34" é uma
                frase incompleta; "faz 220 na wave de 2026-10-18" é uma frase que alguém pode checar
                daqui a seis meses. Waves diferentes podem dar índices diferentes — e até outros
                territórios.
              </p>
            </Term>

            <Term id="horizonte">
              <p>
                O prazo do objetivo: "aumentar consideração em 90 dias". Ele{' '}
                <strong>não entra em nenhuma conta</strong> — nenhum índice muda se ele mudar. Serve
                para a leitura estratégica propor ações do tamanho certo: o que cabe em 30 dias não é
                o que cabe em um ano.
              </p>
            </Term>

            <Term id="procedencia">
              <p>
                Nenhum número é anônimo. Cada um carrega de onde veio — fonte, wave, consulta — e
                quão firme é — base, célula, intervalo.
              </p>
              <p>
                É isso que permite à IA escrever sem inventar: ela cita números pelo endereço e
                nunca os recalcula, e qualquer citação a um número inexistente é descartada. Por
                isso o mesmo brief dá sempre os mesmos números.
              </p>
            </Term>
          </Section>

          <Section id="leitura">
            <p className="dictionary-lead">
              Três seções escritas pela IA a partir dos números da análise. Ela cita números pelo
              endereço e nunca calcula um — ver <a href="#procedencia">procedência</a>. A leitura
              é gerada quando alguém pede e não se atualiza sozinha: se a permissão ou os pesos
              mudarem, é preciso gerar outra.
            </p>

            <Term id="racional" tag="01">
              <Question>O que os dados dizem?</Question>
              <p>
                O diagnóstico: quem é a audiência, onde ela se distingue e onde a marca está contra
                os concorrentes. Cada afirmação traz pelo menos um número da análise como prova.
                Não propõe ação.
              </p>
              <Example>
                "Funk é território da audiência (afinidade 220), e a Pepsi chegou antes (afinidade competitiva
                150)."
              </Example>
            </Term>

            <Term id="playbook" tag="02">
              <Question>O que fazer com isso?</Question>
              <p>
                Ações que dá para executar — "patrocinar X", "criar conteúdo sobre Y com Z", nunca
                "engajar o público". Cada uma fica presa a um território e precisa dizer{' '}
                <strong>como move o KPI do brief</strong>: sem essa ponte, o playbook vira lista de
                ideias. O horizonte do brief dá o tamanho da ação.
              </p>
              <Example>
                "Patrocinar bailes funk com ativação da marca, para disputar consideração onde a
                Pepsi já educou o público." Território: funk. KPI: consideração. Prazo: 90 dias.
              </Example>
              <p>
                A diferença para o racional: o racional explica o terreno; o playbook escolhe onde
                pisar. Uma afirmação pode não virar ação — e toda ação tem de se apoiar em alguma
                afirmação.
              </p>
            </Term>

            <Term id="ressalvas" tag="03">
              <Question>Onde o número pode enganar?</Question>
              <p>
                Onde o dado é <a href="#fragilidade">frágil</a> e o que muda na decisão se alguém agir
                como se ele fosse sólido. Vem vazia quando os números não têm fragilidade — não é
                seção de praxe.
              </p>
            </Term>
          </Section>

          <Section id="limiares">
            <p>Valores padrão em vigor.</p>
            <table className="dictionary-table">
              <thead><tr><th>Limiar</th><th>Padrão</th><th>O que decide</th></tr></thead>
              <tbody>
                <tr><td>Base da audiência</td><td>150 pessoas</td><td>abaixo disso, o formulário não deixa rodar</td></tr>
                <tr><td>Base da categoria</td><td>1.500 pessoas</td><td>abaixo disso, o brief é recusado antes de consultar</td></tr>
                <tr><td>Base mínima</td><td>50 pessoas</td><td>abaixo disso, o número é suprimido</td></tr>
                <tr><td>Base baixa</td><td>100 pessoas</td><td>entre 50 e 100, aparece com alerta</td></tr>
                <tr><td>Célula mínima</td><td>10 pessoas</td><td>abaixo disso, o cruzamento é suprimido ou marcado como frágil</td></tr>
                <tr><td>Confiança</td><td>95%</td><td>intervalo e corte de significância</td></tr>
                <tr><td>Afinidade</td><td>110</td><td>abaixo disso, descarte</td></tr>
                <tr><td>Afinidade competitiva</td><td>110</td><td>acima disso, território disputado</td></tr>
                <tr><td>Ocupação</td><td>110</td><td>acima disso, a marca está no território</td></tr>
              </tbody>
            </table>
          </Section>
        </article>
      </div>
    </>
  )
}

function Section({ id, children }: { id: string; children: ReactNode }) {
  return (
    <section className="dictionary-section" id={id}>
      <h2>{TITLES.get(id)}</h2>
      {children}
    </section>
  )
}

function Term({ id, tag, children }: { id: string; tag?: string; children: ReactNode }) {
  return (
    <div className="dictionary-term" id={id}>
      <h3>{TITLES.get(id)}{tag && <span>{tag}</span>}</h3>
      {children}
    </div>
  )
}

function Question({ children }: { children: ReactNode }) {
  return <p className="dictionary-question">{children}</p>
}

function Reference({ children }: { children: ReactNode }) {
  return <p className="dictionary-meta"><strong>Comparado com:</strong> {children}</p>
}

function Example({ children }: { children: ReactNode }) {
  return <p className="dictionary-example">{children}</p>
}

function Limit({ children }: { children: ReactNode }) {
  return <p className="dictionary-meta"><strong>O que não diz:</strong> {children}</p>
}
